import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

const SB_URL   = import.meta.env.VITE_SUPABASE_URL   as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

interface QualityRow {
  id:              string;
  name:            string;
  category:        string | null;
  asset_status:    string | null;
  pbr_confidence:  number | null;
  qa_score:        number | null;
  qa_status:       string | null;
  geometry_flags:  string[] | null;
  pbr_flags:       string[] | null;
  glb_assembly_flags: string[] | null;
  created_at:      string;
  updated_at:      string | null;
}

type SortKey = "pbr_confidence" | "qa_score" | "name" | "created_at";

function confidenceLabel(v: number | null): { text: string; cls: string } {
  if (v == null) return { text: "—",   cls: "text-gray-500" };
  if (v >= 0.85) return { text: `${(v*100).toFixed(0)}%`, cls: "text-green-400" };
  if (v >= 0.70) return { text: `${(v*100).toFixed(0)}%`, cls: "text-amber-400" };
  return             { text: `${(v*100).toFixed(0)}%`, cls: "text-red-400" };
}

function qaScoreLabel(v: number | null): { text: string; cls: string } {
  if (v == null) return { text: "—",   cls: "text-gray-500" };
  if (v >= 0.75) return { text: `${(v*100).toFixed(0)}%`, cls: "text-green-400" };
  if (v >= 0.50) return { text: `${(v*100).toFixed(0)}%`, cls: "text-amber-400" };
  return             { text: `${(v*100).toFixed(0)}%`, cls: "text-red-400" };
}

function assetAge(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days === 0) return "today";
  if (days === 1) return "1d";
  if (days < 30)  return `${days}d`;
  return `${Math.floor(days / 30)}mo`;
}

function exportCSV(rows: QualityRow[]) {
  const header = "name,category,asset_status,pbr_confidence,qa_score,qa_status,geometry_flags,glb_assembly_flags,asset_age,created_at";
  const body = rows.map((r) => [
    JSON.stringify(r.name),
    r.category ?? "",
    r.asset_status ?? "",
    r.pbr_confidence != null ? (r.pbr_confidence * 100).toFixed(1) : "",
    r.qa_score != null ? (r.qa_score * 100).toFixed(1) : "",
    r.qa_status ?? "",
    (r.geometry_flags ?? []).join("|"),
    (r.glb_assembly_flags ?? []).join("|"),
    assetAge(r.created_at),
    r.created_at,
  ].join(",")).join("\n");

  const blob = new Blob([header + "\n" + body], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `fyrris-quality-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminQuality() {
  const { session } = useAuth();
  const { toast }   = useToast();

  const [rows,     setRows]     = useState<QualityRow[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);
  const [sortKey,  setSortKey]  = useState<SortKey>("pbr_confidence");
  const [sortAsc,  setSortAsc]  = useState(true);   // ascending = worst first for quality
  const [retriggering, setRetriggering] = useState<Record<string, boolean>>({});

  const jwt = session?.access_token ?? "";

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${SB_URL}/rest/v1/products` +
          `?select=id,name,category,asset_status,pbr_confidence,qa_score,qa_status,geometry_flags,pbr_flags,glb_assembly_flags,created_at,updated_at` +
          `&order=created_at.desc`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } },
      );
      if (!res.ok) throw new Error(`${res.status} ${await res.text().catch(() => "")}`);
      setRows(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [jwt]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  async function retriggerPbr(productId: string, productName: string) {
    setRetriggering((prev) => ({ ...prev, [productId]: true }));
    try {
      const res = await fetch(`${SB_URL}/functions/v1/trigger-pbr-pipeline`, {
        method:  "POST",
        headers: {
          apikey:         ANON_KEY,
          Authorization:  `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ product_id: productId }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      toast({ title: "PBR retriggered", description: productName });
      // Refresh after a short delay
      setTimeout(fetchRows, 1500);
    } catch (err) {
      toast({ title: "Failed", description: err instanceof Error ? err.message : "Unknown error", variant: "destructive" });
    } finally {
      setRetriggering((prev) => ({ ...prev, [productId]: false }));
    }
  }

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      let va: string | number | null, vb: string | number | null;
      if (sortKey === "pbr_confidence") { va = a.pbr_confidence; vb = b.pbr_confidence; }
      else if (sortKey === "qa_score")  { va = a.qa_score;       vb = b.qa_score;       }
      else if (sortKey === "name")      { va = a.name;           vb = b.name;           }
      else                              { va = a.created_at;     vb = b.created_at;     }

      if (va == null && vb == null) return 0;
      if (va == null) return sortAsc ? -1 : 1;
      if (vb == null) return sortAsc ?  1 : -1;
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ?  1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sortKey, sortAsc]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortAsc((a) => !a);
    else { setSortKey(key); setSortAsc(true); }
  }

  function SortBtn({ col, label }: { col: SortKey; label: string }) {
    const active = sortKey === col;
    return (
      <button
        onClick={() => toggleSort(col)}
        className={`flex items-center gap-1 text-left text-xs font-medium uppercase tracking-wide transition ${
          active ? "text-teal-400" : "text-gray-500 hover:text-gray-300"
        }`}
      >
        {label}
        {active && <span className="text-xs">{sortAsc ? "↑" : "↓"}</span>}
      </button>
    );
  }

  const highCount   = rows.filter((r) => (r.pbr_confidence ?? 0) >= 0.85).length;
  const goodCount   = rows.filter((r) => (r.pbr_confidence ?? -1) >= 0.70 && (r.pbr_confidence ?? 0) < 0.85).length;
  const lowCount    = rows.filter((r) => r.pbr_confidence != null && r.pbr_confidence < 0.70).length;
  const noDataCount = rows.filter((r) => r.pbr_confidence == null).length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Asset Quality</h1>
          <p className="mt-0.5 text-sm text-gray-400">
            Sorted worst-first by default — identify products that need better source photos.
          </p>
        </div>
        <button
          onClick={() => exportCSV(sorted)}
          disabled={sorted.length === 0}
          className="rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-300 transition hover:bg-gray-800 disabled:opacity-40"
        >
          Export CSV
        </button>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-3">
        {[
          { label: "High (≥85%)",   count: highCount,   cls: "text-green-400" },
          { label: "Good (70–85%)", count: goodCount,   cls: "text-amber-400" },
          { label: "Low (<70%)",    count: lowCount,    cls: "text-red-400"   },
          { label: "No data",       count: noDataCount, cls: "text-gray-500"  },
        ].map(({ label, count, cls }) => (
          <div key={label} className="rounded-lg border border-gray-800 bg-gray-900/60 px-4 py-2 text-center min-w-[90px]">
            <p className={`text-xl font-semibold tabular-nums ${cls}`}>{count}</p>
            <p className="text-xs text-gray-500">{label}</p>
          </div>
        ))}
      </div>

      {error && (
        <div className="rounded-lg border border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-400">
          {error}
        </div>
      )}

      {loading ? (
        <div className="rounded-xl border border-gray-800 py-16 text-center text-sm text-gray-500">
          Loading quality report…
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-800">
          <table className="w-full text-sm">
            <thead className="bg-gray-900/60">
              <tr className="border-b border-gray-800">
                <th className="px-4 py-3 text-left"><SortBtn col="name" label="Product" /></th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Status</th>
                <th className="px-4 py-3 text-left"><SortBtn col="pbr_confidence" label="PBR Confidence" /></th>
                <th className="px-4 py-3 text-left"><SortBtn col="qa_score" label="QA Score" /></th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Flags</th>
                <th className="px-4 py-3 text-left"><SortBtn col="created_at" label="Age" /></th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => {
                const conf    = confidenceLabel(r.pbr_confidence);
                const qas     = qaScoreLabel(r.qa_score);
                const allFlags = [...(r.geometry_flags ?? []), ...(r.pbr_flags ?? []), ...(r.glb_assembly_flags ?? [])];
                const flagsDisplay = allFlags.slice(0, 3).join(", ");
                const moreFlags    = allFlags.length > 3 ? ` +${allFlags.length - 3}` : "";
                return (
                  <tr key={r.id} className="border-t border-gray-800 hover:bg-gray-900/30 transition">
                    <td className="px-4 py-3">
                      <a
                        href={`/admin/products/${r.id}`}
                        className="font-medium text-gray-100 hover:text-teal-400 transition"
                      >
                        {r.name}
                      </a>
                      {r.category && (
                        <p className="text-xs text-gray-500">{r.category.replace(/_/g, " ")}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium ${
                        r.asset_status === "ready"            ? "text-green-400" :
                        r.asset_status === "processing"       ? "text-amber-400" :
                        r.asset_status === "failed_permanent" ? "text-red-500"   :
                        "text-gray-500"
                      }`}>
                        {r.asset_status ?? "—"}
                      </span>
                    </td>
                    <td className={`px-4 py-3 font-semibold tabular-nums ${conf.cls}`}>
                      {conf.text}
                    </td>
                    <td className={`px-4 py-3 font-semibold tabular-nums ${qas.cls}`}>
                      {qas.text}
                    </td>
                    <td className="px-4 py-3 max-w-[220px]">
                      {allFlags.length > 0 ? (
                        <span className="text-xs text-gray-500" title={allFlags.join(", ")}>
                          {flagsDisplay}{moreFlags}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-700">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 tabular-nums">
                      {assetAge(r.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => retriggerPbr(r.id, r.name)}
                        disabled={!!retriggering[r.id]}
                        className="rounded border border-gray-700 px-2.5 py-1 text-xs text-gray-400 transition hover:border-teal-700 hover:text-teal-400 disabled:opacity-40"
                      >
                        {retriggering[r.id] ? "…" : "Retrigger PBR"}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-sm text-gray-500">
                    No products found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
